import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { SlackConnectionAction } from "./SlackConnectionAction";

import DeleteConfirmationModal from "@/components/DeleteConfirmationModal";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Slack } from "@/components/ui/icons";
import { Skeleton } from "@/components/ui/skeleton";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums/toast";
import { useDisconnectSlack, useSlackInstallation, useSlackInstallUrl } from "@/queries/slack";
import { displayToast } from "@/utils/toast";

const SLACK_STATUS_MESSAGES: Record<string, { message: string; type: ToastType }> = {
  connected: { message: "Slack workspace connected", type: ToastType.success },
  already_connected: {
    message: "This organization is already connected to a Slack workspace. Disconnect it first.",
    type: ToastType.error,
  },
  connected_elsewhere: {
    message:
      "This Slack workspace is already connected to another organization. Disconnect it there first.",
    type: ToastType.error,
  },
  error: { message: "Failed to connect Slack workspace", type: ToastType.error },
};

export const SlackSettings = () => {
  const userContext = useUser();
  const workspaceId = userContext?.activeWorkspaceId ?? null;
  const [searchParams, setSearchParams] = useSearchParams();
  const [openDisconnectModal, setOpenDisconnectModal] = useState(false);
  const [statusKey] = useState<string | null>(() => searchParams.get("slack"));
  const statusToastFired = useRef(false);

  const { data, isLoading } = useSlackInstallation(workspaceId);
  const installation = data?.installation ?? null;
  const isOwner = data?.isOwner ?? false;
  const installUrl = useSlackInstallUrl();
  const disconnectSlack = useDisconnectSlack(workspaceId);

  // Read the OAuth result from the URL once, then strip the param so a refresh doesn't replay it.
  useEffect(() => {
    if (searchParams.get("slack")) {
      searchParams.delete("slack");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Surface the result as a toast. This view loads cold via the OAuth redirect, so the toast must be
  // deferred a tick — otherwise it fires before the ancestor <Toaster> mounts and gets dropped.
  useEffect(() => {
    if (statusToastFired.current || !statusKey) {
      return;
    }
    statusToastFired.current = true;
    const info = SLACK_STATUS_MESSAGES[statusKey] ?? SLACK_STATUS_MESSAGES.error;
    setTimeout(() => displayToast(info.message, info.type), 0);
  }, [statusKey]);

  const handleAddToSlack = async () => {
    if (workspaceId === null) {
      return;
    }
    try {
      const url = await installUrl.mutateAsync(workspaceId);
      window.location.assign(url);
    } catch {
      displayToast("Failed to start Slack installation", ToastType.error);
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectSlack.mutateAsync();
      displayToast("Slack workspace disconnected", ToastType.success);
    } catch {
      displayToast("Failed to disconnect Slack workspace", ToastType.error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-y-1">
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {installation && <Slack className="h-6 w-6" />}
            {installation ? installation.slack_team_name || "Slack workspace" : "Add to Slack"}
          </CardTitle>
          <CardDescription>
            {installation ? (
              <>
                Mention{" "}
                <span className="rounded-[3px] bg-[#e8f4f9] px-[3px] font-medium text-[#3596e0]">
                  @Wrangler
                </span>{" "}
                in any channel to chat with it. Team members link their PromptLayer account on first
                use.
              </>
            ) : (
              "Connect your organization's Slack so anyone can chat with Wrangler from a channel."
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <SlackConnectionAction
            isOwner={isOwner}
            hasInstallation={installation !== null}
            isInstallPending={installUrl.isPending}
            onAddToSlack={handleAddToSlack}
            onDisconnect={() => setOpenDisconnectModal(true)}
          />
        </CardContent>
      </Card>
      <DeleteConfirmationModal
        title="Disconnect Slack"
        description="Wrangler will stop responding in this Slack workspace. You can reconnect at any time."
        open={openDisconnectModal}
        setOpen={setOpenDisconnectModal}
        onDelete={handleDisconnect}
        confirmLabel="Disconnect"
      />
    </div>
  );
};
